import { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, Calculator, Download, ImagePlus, X, Sparkles, Mail, CheckCircle2 } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";

const FREQUENCIES = [
  { value: "turnover", label: "Airbnb / STR turnover" },
  { value: "weekly", label: "Weekly recurring" },
  { value: "biweekly", label: "Bi-weekly recurring" },
  { value: "monthly", label: "Monthly recurring" },
  { value: "deep", label: "Deep clean" },
  { value: "move_out", label: "Move-out clean" },
  { value: "post_construction", label: "Post-construction clean" },
];

const LAUNDRY_OPTIONS = [
  { value: "none", label: "No laundry" },
  { value: "light", label: "Light — 1 load" },
  { value: "standard", label: "Standard — linens & towels" },
  { value: "heavy", label: "Heavy — multiple loads" },
];

const SOIL_OPTIONS = [
  { value: "light", label: "Light — tidy" },
  { value: "average", label: "Average" },
  { value: "heavy", label: "Heavy — extra dirty" },
];

const blank = {
  bedrooms: "",
  beds: "",
  bathrooms: "",
  sqft: "",
  city: "",
  state: "",
  frequency: "turnover",
  pets: "",
  guests: "",
  laundry: "none",
  soil_level: "average",
  grill: false,
  hot_tub: false,
  pool: false,
};

export const EstimateCalculator = () => {
  const [form, setForm] = useState(blank);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [result, setResult] = useState(null);
  const [lead, setLead] = useState({ name: "", email: "", phone: "" });
  const [savingLead, setSavingLead] = useState(false);
  const [leadSent, setLeadSent] = useState(false);

  const onPhotos = (e) => {
    const files = Array.from(e.target.files || []).slice(0, 5);
    setPhotos(files);
  };

  const propertyFields = () => ({
    bedrooms: parseFloat(form.bedrooms) || 0,
    beds: parseFloat(form.beds) || 0,
    bathrooms: parseFloat(form.bathrooms) || 0,
    sqft: parseFloat(form.sqft) || 0,
    city: form.city,
    state: form.state,
    frequency: form.frequency,
    pets: parseFloat(form.pets) || 0,
    guests: parseFloat(form.guests) || 0,
    laundry: form.laundry,
    soil_level: form.soil_level,
    grill: form.grill,
    hot_tub: form.hot_tub,
    pool: form.pool,
  });

  const calculate = async () => {
    if (!form.city.trim() || !form.state.trim())
      return toast.error("Enter a city and state");
    setLoading(true);
    setResult(null);
    try {
      const fd = new FormData();
      Object.entries(propertyFields()).forEach(([k, v]) => fd.append(k, String(v)));
      photos.forEach((p) => fd.append("photos", p));
      const { data } = await api.post("/estimate", fd);
      setResult(data);
      setLeadSent(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not generate an estimate");
    } finally {
      setLoading(false);
    }
  };

  const captureLead = async () => {
    if (!lead.email.trim()) return toast.error("Enter your email");
    setSavingLead(true);
    try {
      const payload = { ...lead, ...propertyFields(), ...result };
      const { data } = await api.post("/leads", payload);
      setLeadSent(true);
      toast.success(data.message || "Thanks! We'll be in touch.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not submit. Check your email and try again.");
    } finally {
      setSavingLead(false);
    }
  };

  const downloadQuote = async () => {
    setDownloading(true);
    try {
      const payload = { ...propertyFields(), ...result };
      const res = await api.post("/estimate/quote.pdf", payload, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "cleaning-estimate.pdf";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Could not download the quote");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      {/* Form */}
      <Card className="p-6">
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Bedrooms</Label>
            <Input
              type="number"
              value={form.bedrooms}
              onChange={(e) => setForm({ ...form, bedrooms: e.target.value })}
              placeholder="3"
              data-testid="estimate-bedrooms-input"
            />
          </div>
          <div className="space-y-2">
            <Label>Beds</Label>
            <Input
              type="number"
              value={form.beds}
              onChange={(e) => setForm({ ...form, beds: e.target.value })}
              placeholder="4"
              data-testid="estimate-beds-input"
            />
          </div>
          <div className="space-y-2">
            <Label>Bathrooms</Label>
            <Input
              type="number"
              value={form.bathrooms}
              onChange={(e) => setForm({ ...form, bathrooms: e.target.value })}
              placeholder="2"
              data-testid="estimate-bathrooms-input"
            />
          </div>
          <div className="space-y-2">
            <Label>Square footage</Label>
            <Input
              type="number"
              value={form.sqft}
              onChange={(e) => setForm({ ...form, sqft: e.target.value })}
              placeholder="1800"
              data-testid="estimate-sqft-input"
            />
          </div>
          <div className="space-y-2">
            <Label>City</Label>
            <Input
              value={form.city}
              onChange={(e) => setForm({ ...form, city: e.target.value })}
              placeholder="Austin"
              data-testid="estimate-city-input"
            />
          </div>
          <div className="space-y-2">
            <Label>State</Label>
            <Input
              value={form.state}
              onChange={(e) => setForm({ ...form, state: e.target.value })}
              placeholder="TX"
              data-testid="estimate-state-input"
            />
          </div>
        </div>

        <div className="space-y-2 mt-4">
          <Label>Cleaning frequency</Label>
          <Select
            value={form.frequency}
            onValueChange={(v) => setForm({ ...form, frequency: v })}
          >
            <SelectTrigger data-testid="estimate-frequency-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {FREQUENCIES.map((f) => (
                <SelectItem key={f.value} value={f.value}>
                  {f.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="grid grid-cols-2 gap-4 mt-4">
          <div className="space-y-2">
            <Label>Pets</Label>
            <Input
              type="number"
              min="0"
              value={form.pets}
              onChange={(e) => setForm({ ...form, pets: e.target.value })}
              placeholder="0"
              data-testid="estimate-pets-input"
            />
          </div>
          <div className="space-y-2">
            <Label>Max guests</Label>
            <Input
              type="number"
              min="0"
              value={form.guests}
              onChange={(e) => setForm({ ...form, guests: e.target.value })}
              placeholder="6"
              data-testid="estimate-guests-input"
            />
          </div>
          <div className="space-y-2">
            <Label>Laundry</Label>
            <Select value={form.laundry} onValueChange={(v) => setForm({ ...form, laundry: v })}>
              <SelectTrigger data-testid="estimate-laundry-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {LAUNDRY_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Soil level</Label>
            <Select value={form.soil_level} onValueChange={(v) => setForm({ ...form, soil_level: v })}>
              <SelectTrigger data-testid="estimate-soil-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SOIL_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="mt-4 space-y-2">
          <Label>Add-on services</Label>
          <div className="flex flex-wrap gap-4">
            <label className="flex items-center gap-2 cursor-pointer text-sm" data-testid="estimate-grill-label">
              <Checkbox
                checked={form.grill}
                onCheckedChange={(v) => setForm({ ...form, grill: !!v })}
                data-testid="estimate-grill-checkbox"
              />
              Grill / BBQ cleaning
            </label>
            <label className="flex items-center gap-2 cursor-pointer text-sm" data-testid="estimate-hottub-label">
              <Checkbox
                checked={form.hot_tub}
                onCheckedChange={(v) => setForm({ ...form, hot_tub: !!v })}
                data-testid="estimate-hottub-checkbox"
              />
              Hot tub
            </label>
            <label className="flex items-center gap-2 cursor-pointer text-sm" data-testid="estimate-pool-label">
              <Checkbox
                checked={form.pool}
                onCheckedChange={(v) => setForm({ ...form, pool: !!v })}
                data-testid="estimate-pool-checkbox"
              />
              Pool
            </label>
          </div>
        </div>

        <div className="space-y-2 mt-4">
          <Label>Photos (optional, up to 5)</Label>
          <label
            className="flex items-center gap-2 border border-dashed border-border rounded-lg px-4 py-3 cursor-pointer hover:bg-muted text-sm text-muted-foreground"
            data-testid="estimate-photos-label"
          >
            <ImagePlus className="h-4 w-4" />
            {photos.length ? `${photos.length} photo(s) selected` : "Add photos to sharpen the estimate"}
            <input
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={onPhotos}
              data-testid="estimate-photos-input"
            />
          </label>
          {photos.length > 0 && (
            <div className="flex flex-wrap gap-2 pt-1">
              {photos.map((p, i) => (
                <span
                  key={i}
                  className="text-xs bg-secondary text-secondary-foreground rounded-full px-2 py-1 flex items-center gap-1"
                >
                  {p.name.slice(0, 16)}
                  <button
                    type="button"
                    onClick={() => setPhotos(photos.filter((_, idx) => idx !== i))}
                    aria-label="Remove photo"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>

        <Button
          className="w-full h-11 mt-6 text-base"
          onClick={calculate}
          disabled={loading}
          data-testid="calculate-estimate-button"
        >
          {loading ? (
            <Loader2 className="h-5 w-5 mr-2 animate-spin" />
          ) : (
            <Calculator className="h-5 w-5 mr-2" />
          )}
          Calculate estimate
        </Button>
      </Card>

      {/* Result */}
      <Card className="p-6 flex flex-col" data-testid="estimate-result-card">
        {!result && !loading && (
          <div className="flex-1 flex flex-col items-center justify-center text-center text-muted-foreground py-10">
            <Sparkles className="h-8 w-8 mb-3 text-primary" strokeWidth={1.5} />
            <p className="text-sm max-w-xs">
              Fill in the property details and we'll estimate a fair cost per cleaning for your
              city — photos make it more accurate.
            </p>
          </div>
        )}
        {loading && (
          <div className="flex-1 flex flex-col items-center justify-center text-muted-foreground py-10">
            <Loader2 className="h-8 w-8 animate-spin text-primary mb-3" />
            <p className="text-sm">Analyzing your property…</p>
          </div>
        )}
        {result && (
          <div className="flex-1" data-testid="estimate-result">
            <p className="text-xs tracking-[0.15em] uppercase font-bold text-primary mb-2">
              Estimated cost per cleaning
            </p>
            <div className="flex items-end gap-3">
              <span className="font-display text-5xl font-semibold" data-testid="estimate-typical">
                ${Math.round(result.typical)}
              </span>
              <span className="text-muted-foreground mb-2 text-sm">
                range ${Math.round(result.low)}–${Math.round(result.high)}
              </span>
            </div>
            {result.rationale && (
              <p className="text-sm text-muted-foreground mt-4 leading-relaxed">
                {result.rationale}
              </p>
            )}
            {result.factors?.length > 0 && (
              <ul className="mt-4 space-y-2">
                {result.factors.map((f, i) => (
                  <li key={i} className="text-sm flex items-start gap-2">
                    <span className="h-1.5 w-1.5 rounded-full bg-primary mt-1.5 shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>
            )}
            <Button
              variant="outline"
              className="w-full mt-6"
              onClick={downloadQuote}
              disabled={downloading}
              data-testid="download-quote-button"
            >
              {downloading ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Download className="h-4 w-4 mr-2" />
              )}
              Download quote (PDF)
            </Button>

            {/* Lead capture */}
            <div className="mt-6 pt-5 border-t border-border" data-testid="lead-capture">
              {leadSent ? (
                <div className="flex items-center gap-2 text-sm text-emerald-700" data-testid="lead-sent">
                  <CheckCircle2 className="h-5 w-5" /> Thanks! We've got your details and will be in touch.
                </div>
              ) : (
                <>
                  <p className="text-sm font-medium flex items-center gap-2">
                    <Mail className="h-4 w-4 text-primary" /> Email me this quote
                  </p>
                  <p className="text-xs text-muted-foreground mt-1 mb-3">
                    Get a copy in your inbox — no obligation.
                  </p>
                  <div className="grid sm:grid-cols-2 gap-2">
                    <Input
                      placeholder="Your name"
                      value={lead.name}
                      onChange={(e) => setLead({ ...lead, name: e.target.value })}
                      data-testid="lead-name-input"
                    />
                    <Input
                      type="email"
                      placeholder="you@email.com"
                      value={lead.email}
                      onChange={(e) => setLead({ ...lead, email: e.target.value })}
                      data-testid="lead-email-input"
                    />
                  </div>
                  <Input
                    className="mt-2"
                    placeholder="Phone (optional)"
                    value={lead.phone}
                    onChange={(e) => setLead({ ...lead, phone: e.target.value })}
                    data-testid="lead-phone-input"
                  />
                  <Button
                    className="w-full mt-3"
                    onClick={captureLead}
                    disabled={savingLead}
                    data-testid="lead-submit-button"
                  >
                    {savingLead && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                    Email me this quote
                  </Button>
                </>
              )}
            </div>
            <p className="text-[11px] text-muted-foreground mt-3 text-center">
              Automated estimate · final pricing may vary after an on-site assessment.
            </p>
          </div>
        )}
      </Card>
    </div>
  );
};
